// src/components/Layout/Sidebar.tsx
import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Squares2X2Icon,
  UserGroupIcon,
  ClockIcon,
  ArrowRightOnRectangleIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../../contexts/AuthContext";
import logo from "../../assets/logo.png";
import ConfirmationModal from "../UI/ConfirmationModal";

const navigation = [
  { name: "Dashboard", href: "/", icon: Squares2X2Icon },
  { name: "Users", href: "/users", icon: UserGroupIcon },
  { name: "Sessions", href: "/sessions", icon: ClockIcon },
];

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { logout, userData, currentUser } = useAuth();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      console.error("Failed to logout:", error);
    } finally {
      setIsLoggingOut(false);
      setShowLogoutConfirm(false);
    }
  };

  const confirmLogout = () => {
    setShowLogoutConfirm(true);
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  return (
    <>
      <div className="flex flex-col w-64 bg-gray-900 h-screen sticky top-0">
        {/* Logo Section - No scroll */}
        <div className="px-6 py-8 border-b border-gray-800 flex-shrink-0">
          <div className="flex flex-col items-center">
            <div className="mb-4">
              <div className="h-16 w-16 rounded-lg bg-gray-800 flex items-center justify-center border border-gray-700">
                <img
                  src={logo}
                  alt="The Black Map Logo"
                  className="h-10 w-10 object-contain"
                />
              </div>
            </div>
            <div className="text-center">
              <h1 className="text-xl font-bold text-white tracking-tight">
                THE BLACK
              </h1>
              <h1 className="text-xl font-bold text-amber-500 tracking-tight">
                MAP
              </h1>
              <p className="text-xs text-gray-400 mt-1">Admin Dashboard</p>
            </div>
          </div>
        </div>

        {/* Navigation - Scrollable if needed */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <NavLink
                key={item.name}
                to={item.href}
                className={`
                  flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200
                  ${
                    isActive
                      ? "bg-gray-800 text-amber-500 border-l-4 border-amber-500"
                      : "text-gray-300 hover:text-white hover:bg-gray-800/50"
                  }
                `}
              >
                <item.icon
                  className={`h-5 w-5 mr-3 ${
                    isActive ? "text-amber-500" : "text-gray-400"
                  }`}
                />
                <span className="flex-1">{item.name}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* Logout Section - Fixed at bottom */}
        <div className="p-4 border-t border-gray-800 flex-shrink-0">
          <button
            onClick={confirmLogout}
            className="flex items-center w-full px-4 py-3 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-all duration-200"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3 text-gray-400" />
            <span className="flex-1 text-left">Logout</span>
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showLogoutConfirm}
        onClose={cancelLogout}
        onConfirm={handleLogout}
        title="Confirm Logout"
        message="Are you sure you want to logout from the admin dashboard?"
        confirmText="Logout"
        cancelText="Cancel"
        variant="danger"
        isLoading={isLoggingOut}
      />
    </>
  );
};

export default Sidebar;
